import { Box, Button, Card, IconButton, TextField } from '@mui/material'

import { useState } from 'react'
import WTypography from 'Components/whiteTypography'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import urls from 'utils/urls'
import { makePostRequest } from 'utils/api'
import { useTranslation } from 'react-i18next'

// LAP-PAT1 — Simulateur de décision (« Puis-je faire cet achat sans retarder
// mes objectifs ? »). Volontairement générique : un « bien » (prix, apport,
// durée) + des postes de dépense mensuels LIÉS ajoutés librement.
// Calcul côté back : POST /api/Finance/decision-simulation/.

const inputSx = {
  '& .MuiInputBase-input': { color: 'white', fontSize: '13px', py: '6px' },
  '& .MuiInputLabel-root': { color: '#ffffffa1', fontSize: '12px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff33' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff66' },
}

const VERDICT_STYLES = {
  compatible: { color: '#8be28b', bg: '#8be28b1a', border: '#8be28b55' },
  delays_objective: { color: '#ffb36b', bg: '#ffb36b1a', border: '#ffb36b55' },
  unsustainable: { color: '#ff9c9c', bg: '#ff9c9c1a', border: '#ff9c9c55' },
}

function formatEUR(value) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0))
}

function formatMonth(iso, locale) {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleDateString(locale || 'fr-FR', {
      month: 'long',
      year: 'numeric',
    })
  } catch (e) {
    return iso
  }
}

function ImpactTile({ label, value, color }) {
  return (
    <Box
      sx={{
        flex: '1 1 120px',
        minWidth: '110px',
        p: 1,
        borderRadius: '8px',
        border: '1px solid #ffffff22',
        backgroundColor: '#ffffff0d',
      }}
    >
      <WTypography sx={{ fontSize: '11px', color: '#ffffffa1' }}>{label}</WTypography>
      <WTypography sx={{ fontSize: '14px', fontWeight: 600, color: color || 'white' }}>
        {value}
      </WTypography>
    </Box>
  )
}

function SimulateurDecision({ oldData, updateReports }) {
  const { t, i18n } = useTranslation('layout')
  const [price, setPrice] = useState('')
  const [downPayment, setDownPayment] = useState('')
  const [duration, setDuration] = useState('')
  const [linkedCosts, setLinkedCosts] = useState([])
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)

  const addCost = () => setLinkedCosts([...linkedCosts, { name: '', amount: '' }])
  const updateCost = (idx, field, value) => {
    const next = [...linkedCosts]
    next[idx] = { ...next[idx], [field]: value }
    setLinkedCosts(next)
  }
  const removeCost = (idx) => setLinkedCosts(linkedCosts.filter((_, i) => i !== idx))

  const simulate = async () => {
    if (!price || Number(price) <= 0) return
    setLoading(true)
    try {
      const response = await makePostRequest(
        urls.financeDecisionSimulation,
        {
          price: Number(price),
          down_payment: Number(downPayment) || 0,
          duration_months: Number(duration) || 0,
          linked_costs: linkedCosts
            .filter((c) => c.name || c.amount)
            .map((c) => ({ name: c.name, amount: Number(c.amount) || 0 })),
        },
        {},
        true,
      )
      if (response.status === 200) {
        setResult(response.data)
      }
    } finally {
      setLoading(false)
    }
  }

  const verdictStyle = result ? VERDICT_STYLES[result.verdict] || VERDICT_STYLES.compatible : null
  const locale = i18n.language

  return (
    <Card className="dashboard_card__root1">
      <Box className="dashboard_card__header1">
        <WTypography component={'span'} variant="h6">
          {t('monPatrimoine.decision.title')}
        </WTypography>
        {oldData?.monthly_capacity != null && (
          <WTypography sx={{ fontSize: '12px', color: '#ffffffa1' }}>
            {t('monPatrimoine.savingsCapacity')} : {formatEUR(oldData.monthly_capacity)}/
            {t('monPatrimoine.perMonth')}
          </WTypography>
        )}
      </Box>
      <Box className="dashboard_card__header1">
        <WTypography sx={{ color: '#ffffffa1' }} variant={'body2'}>
          {t('monPatrimoine.decision.subtitle')}
        </WTypography>
      </Box>

      <Box className="no-drag" sx={{ px: 1, pb: 1, overflowY: 'auto', height: '85%' }}>
        {/* Entrées : prix du bien / apport / durée */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          <TextField
            size="small"
            type="number"
            sx={{ ...inputSx, flex: 1, minWidth: '90px' }}
            label={t('monPatrimoine.decision.price')}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
          <TextField
            size="small"
            type="number"
            sx={{ ...inputSx, flex: 1, minWidth: '90px' }}
            label={t('monPatrimoine.decision.downPayment')}
            value={downPayment}
            onChange={(e) => setDownPayment(e.target.value)}
          />
          <TextField
            size="small"
            type="number"
            sx={{ ...inputSx, flex: 1, minWidth: '90px' }}
            label={t('monPatrimoine.decision.durationMonths')}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
        </Box>

        {/* Postes de dépense liés (assurance, entretien, charges…) */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <WTypography sx={{ fontSize: '12px', color: '#ffffffa1', flex: 1 }}>
            {t('monPatrimoine.decision.linkedCosts')}
          </WTypography>
          <IconButton size="small" onClick={addCost}>
            <AddCircleIcon sx={{ color: 'gray', fontSize: '18px' }} />
          </IconButton>
        </Box>
        {linkedCosts.map((cost, idx) => (
          <Box key={idx} sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
            <TextField
              size="small"
              sx={{ ...inputSx, flex: 2 }}
              placeholder={t('monPatrimoine.decision.costName')}
              value={cost.name}
              onChange={(e) => updateCost(idx, 'name', e.target.value)}
            />
            <TextField
              size="small"
              type="number"
              sx={{ ...inputSx, flex: 1 }}
              placeholder={t('monPatrimoine.decision.costAmount')}
              value={cost.amount}
              onChange={(e) => updateCost(idx, 'amount', e.target.value)}
            />
            <IconButton size="small" onClick={() => removeCost(idx)}>
              <RemoveCircleIcon sx={{ color: 'gray', fontSize: '16px' }} />
            </IconButton>
          </Box>
        ))}

        <Button
          variant="contained"
          size="small"
          disabled={loading || !price}
          onClick={simulate}
          sx={{ mt: 1.5, textTransform: 'none' }}
        >
          {loading ? t('monPatrimoine.decision.simulating') : t('monPatrimoine.decision.simulate')}
        </Button>

        {result && (
          <>
            {/* Verdict */}
            <Box
              sx={{
                mt: 1.5,
                p: 1,
                borderRadius: '8px',
                border: `1px solid ${verdictStyle.border}`,
                backgroundColor: verdictStyle.bg,
              }}
            >
              <WTypography sx={{ fontSize: '13px', fontWeight: 600, color: verdictStyle.color }}>
                {t(`monPatrimoine.decision.verdict.${result.verdict}`)}
              </WTypography>
              <WTypography sx={{ fontSize: '12px', color: '#ffffffc7' }}>
                {t('monPatrimoine.decision.realMonthlyCost')} :{' '}
                {formatEUR(result.monthly_cost?.total)}/{t('monPatrimoine.perMonth')}
              </WTypography>
            </Box>

            {/* Impact */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <ImpactTile
                label={t('monPatrimoine.decision.capacityAfter')}
                value={`${formatEUR(result.impact?.capacity_after)}/${t('monPatrimoine.perMonth')}`}
                color={result.impact?.capacity_after >= 0 ? '#8be28b' : '#ff9c9c'}
              />
              {result.impact?.autonomy_after_months != null && (
                <ImpactTile
                  label={t('monPatrimoine.decision.autonomyAfter')}
                  value={`${result.impact.autonomy_after_months} ${t('monPatrimoine.decision.months')}`}
                />
              )}
              <ImpactTile
                label={t('monPatrimoine.decision.wealth10y')}
                value={formatEUR(result.impact?.wealth_10y)}
                color="#ffb36b"
              />
            </Box>

            {/* Trajectoire de l'objectif d'apport */}
            {result.objective && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: '8px',
                  border: '1px solid #ffffff22',
                  backgroundColor: '#ffffff0d',
                }}
              >
                <WTypography sx={{ fontSize: '12px', fontWeight: 600, color: 'white' }}>
                  {result.objective.name}
                </WTypography>
                <WTypography sx={{ fontSize: '11px', color: '#ffffffa1' }}>
                  {t('monPatrimoine.decision.baselineDate')} :{' '}
                  {formatMonth(result.objective.date_baseline, locale) || '—'}
                  {' · '}
                  {t('monPatrimoine.decision.withDecisionDate')} :{' '}
                  {formatMonth(result.objective.date_with_decision, locale) || '—'}
                </WTypography>
                {result.objective.delay_months != null && (
                  <WTypography
                    sx={{
                      fontSize: '11px',
                      color: result.objective.delay_months > 0 ? '#ffb36b' : '#8be28b',
                    }}
                  >
                    {result.objective.delay_months > 0
                      ? `+${result.objective.delay_months} ${t('monPatrimoine.projectionMonthsLate')}`
                      : t('monPatrimoine.projectionOnTime')}
                  </WTypography>
                )}
              </Box>
            )}

            {/* Recommandation */}
            {result.recommendation?.monthly_budget_max != null && (
              <WTypography sx={{ fontSize: '12px', color: '#93b7ff', mt: 1 }}>
                {t('monPatrimoine.decision.recommendation')} :{' '}
                {formatEUR(result.recommendation.monthly_budget_max)}/
                {t('monPatrimoine.perMonth')}
              </WTypography>
            )}

            {/* Alternatives chiffrées */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {(result.alternatives || [])
                .filter((alt) => alt.delay_months != null)
                .map((alt) => (
                  <Box
                    key={alt.type}
                    sx={{
                      flex: '1 1 130px',
                      p: 1,
                      borderRadius: '8px',
                      border: '1px solid #ffffff22',
                    }}
                  >
                    <WTypography sx={{ fontSize: '11px', color: '#ffffffa1' }}>
                      {t(`monPatrimoine.decision.alternatives.${alt.type}`)}
                    </WTypography>
                    <WTypography sx={{ fontSize: '12px', fontWeight: 600, color: 'white' }}>
                      {alt.price != null && formatEUR(alt.price)}
                      {alt.down_payment != null && formatEUR(alt.down_payment)}
                      {alt.duration_months != null &&
                        `${alt.duration_months} ${t('monPatrimoine.decision.months')}`}
                      {alt.defer_months != null &&
                        `${alt.defer_months} ${t('monPatrimoine.decision.months')}`}
                    </WTypography>
                    <WTypography
                      sx={{
                        fontSize: '11px',
                        color: alt.delay_months > 0 ? '#ffb36b' : '#8be28b',
                      }}
                    >
                      {alt.delay_months > 0
                        ? `+${alt.delay_months} ${t('monPatrimoine.projectionMonthsLate')}`
                        : t('monPatrimoine.projectionOnTime')}
                    </WTypography>
                  </Box>
                ))}
            </Box>
          </>
        )}
      </Box>
    </Card>
  )
}

export default SimulateurDecision
